import { useCallback, useEffect, useState } from 'react';

const CACHE_KEY = 'saloni_instagram_feed_v1';
const CACHE_TTL_MS = 15 * 60 * 1000;

function readFeedCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    if (!Array.isArray(parsed.posts)) return null;
    return parsed.posts;
  } catch {
    return null;
  }
}

function writeFeedCache(posts) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), posts }));
  } catch {
    /* quota / private mode */
  }
}

function normalizePosts(rows) {
  return (rows || [])
    .map((row) => ({
      id: String(row?.id || '').trim(),
      image: String(row?.image || '').trim(),
      permalink: String(row?.permalink || '').trim(),
      caption: String(row?.caption || '').trim(),
      media_type: String(row?.media_type || '').trim(),
    }))
    .filter((row) => row.id && row.image && row.permalink);
}

async function fetchInstagramFeed({ limit = 6 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  const r = await fetch(`/api/instagram_feed.php?${params}`, { cache: 'default' });
  const data = await r.json().catch(() => ({}));
  if (!Array.isArray(data?.posts)) {
    return [];
  }
  return normalizePosts(data.posts);
}

/**
 * Recent Instagram posts for the home page (Graph API via server cache).
 * @returns {{ posts: Array<{ id: string, image: string, permalink: string, caption: string }>, loading: boolean, refetch: Function }}
 */
export function useInstagramFeed({ limit = 6, enabled = true } = {}) {
  const [posts, setPosts] = useState(() => (enabled ? readFeedCache() : null) || []);
  const [loading, setLoading] = useState(enabled && posts.length === 0);

  const refetch = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) return [];
    if (!silent) setLoading(true);
    try {
      const next = await fetchInstagramFeed({ limit });
      setPosts(next);
      writeFeedCache(next);
      return next;
    } catch {
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [enabled, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      const cached = readFeedCache();
      if (cached?.length) {
        setPosts(cached);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const next = await fetchInstagramFeed({ limit });
        if (cancelled) return;
        setPosts(next);
        writeFeedCache(next);
      } catch {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, limit]);

  return { posts, loading, refetch };
}
